import { makeStyles } from '@material-ui/styles';
import { useSetAtom } from 'jotai';

import { MainButton } from '@bizzabo/design-system';
import { UserPreview, UserData } from '@bizzabo/entities';

import { getGuestProfileId, getHostProfileId, isHost } from '../../helpers';
import ManageMeetingCard from '../ManageMeetingCard';
import MeetingItem from '../MeetingItem';
import MeetingLinkBtn from '../MeetingLinkBtn';
import useTypeTracking from '../../../../analytics';
import { ScheduledMeeting } from '../../types';
import LocationDetails from '../LocationDetails';
import TimeInfo from '../TimeInfo';
import DateAndTime from '../DateAndTime';
import LocationBadge from '../LocationBadge';
import UserBadgeAction from '../UserBadgeAction';
import { joinVideoAtom } from '../../../../video/atoms';
import { useMeetings } from '../../MeetingsTool/MeetingsToolContext';

interface MeetingNowItemProps {
    meeting: ScheduledMeeting;
    currentUser: UserData;
    user: UserPreview;
}

const MeetingNowItem: React.FC<MeetingNowItemProps> = ({ meeting, currentUser, user }) => {
    const { eventId } = useMeetings();
    const classes = useStyles();
    const setJoinVideo = useSetAtom(joinVideoAtom);
    const isCurrentUserHost = isHost(meeting, currentUser.userProfileId);
    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                meeting_id: meeting.id,
                location: 'MEETING_CARD',
                flow: 'JOIN_MEETING',
                meeting_participant_role: isCurrentUserHost ? 'HOST' : 'GUEST',
                meeting_type: 'SCHEDULED',
            },
        },
    });

    const onJoin = async () => {
        const hostProfileId = getHostProfileId(meeting);
        const guestProfileId = getGuestProfileId(meeting);
        setJoinVideo({
            eventId,
            meetingId: meeting.id,
            hostProfileId,
            guestProfileId,
        });

        trackClickEvent({
            subCategory: 'JOIN_MEETING_NOW',
        });
    };

    const meetingLinkClick = async () => {
        const hostProfileId = getHostProfileId(meeting);
        const guestProfileId = getGuestProfileId(meeting);
        setJoinVideo({
            eventId,
            meetingId: meeting.id,
            hostProfileId,
            guestProfileId,
        });

        trackClickEvent({
            subCategory: 'MEETING_LINK_CLICKED',
        });
    };

    return (
        <Track>
            <ManageMeetingCard>
                <MeetingItem>
                    <TimeInfo>
                        <DateAndTime
                            startDate={meeting.startDate}
                            endDate={meeting.endDate}
                            isNowShow
                        />
                        <LocationBadge locationType={meeting.locationType} />
                    </TimeInfo>
                    <UserBadgeAction user={user} hasNotification={!!meeting.notifications?.length}>
                        <MainButton onClick={onJoin} className={classes.joinBtn}>
                            Join
                        </MainButton>
                    </UserBadgeAction>
                    <LocationDetails physicalLocation={meeting.physicalLocation}>
                        <MeetingLinkBtn
                            meeting={meeting}
                            isHost={isCurrentUserHost}
                            meetingLinkClick={meetingLinkClick}
                        />
                    </LocationDetails>
                </MeetingItem>
            </ManageMeetingCard>
        </Track>
    );
};

const useStyles = makeStyles(
    theme => ({
        joinBtn: {
            ...theme.typography.t81,
            padding: '3px 13px',
            border: 'none',
            color: theme.customization.background.C10,
            backgroundColor: theme.palette.primary[500],
            '&:hover': {
                backgroundColor: theme.palette.primary[400],
            },
        },
    }),
    { classNamePrefix: 'meetings-meet-now-button' }
);

export default MeetingNowItem;
